/* ============================================================
   CHESS — Theme JavaScript
   Vanilla JS, no dependencies, deferred load
   ============================================================ */

'use strict';

/* ── Utilities ──────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function trapFocus(el) {
  const focusable = $$('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])', el);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  el.addEventListener('keydown', e => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
    else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
  });
}

function activeCurrency() {
  return window.__chess?.currency || 'EUR';
}

function money(cents, currency = activeCurrency()) {
  return new Intl.NumberFormat(navigator.language || 'en-GB', {
    style: 'currency', currency, minimumFractionDigits: 0
  }).format(cents / 100);
}

function productMoney(amount, currency = activeCurrency()) {
  const numeric = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (!Number.isFinite(numeric)) return '';
  return new Intl.NumberFormat(navigator.language || 'en-GB', {
    style: 'currency', currency, minimumFractionDigits: 0
  }).format(numeric);
}

function escapeHTML(value = '') {
  return String(value).replace(/[&<>"']/g, char => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[char]);
}

/* ── Theme (dark/light) ─────────────────────────────────────── */
const Theme = {
  key: 'chess-theme',
  get() { return localStorage.getItem(this.key) || document.documentElement.dataset.theme || 'dark'; },
  set(val) {
    localStorage.setItem(this.key, val);
    document.documentElement.dataset.theme = val;
    $$('.js-theme-label').forEach(el => { el.textContent = val === 'dark' ? 'Light' : 'Dark'; });
    const panel = $('.js-a11y-theme');
    if (panel) panel.setAttribute('aria-checked', val === 'dark' ? 'true' : 'false');
  },
  toggle() { this.set(this.get() === 'dark' ? 'light' : 'dark'); },
  init() {
    const saved = localStorage.getItem(this.key);
    if (saved) document.documentElement.dataset.theme = saved;
    this.set(this.get());
    $$('.js-theme-toggle').forEach(btn => btn.addEventListener('click', () => this.toggle()));
  }
};

const Localization = {
  init() {
    $$('[data-footer-locale] form').forEach(form => {
      form.addEventListener('submit', () => {
        form.classList.add('is-submitting');
      });
      form.querySelectorAll('select').forEach(select => {
        select.addEventListener('change', () => {
          form.classList.add('is-submitting');
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        });
      });
    });
  }
};

const FooterService = {
  init() {
    const root = $('[data-footer-service]');
    if (!root) return;
    const drawer = $('.footer-service__drawer', root);
    const panels = $$('[data-footer-panel]', root);
    let lastTrigger = null;

    const close = () => {
      root.hidden = true;
      root.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
      panels.forEach(panel => { panel.hidden = true; });
      if (lastTrigger) lastTrigger.focus();
    };

    const open = (name, trigger = null) => {
      lastTrigger = trigger || lastTrigger;
      panels.forEach(panel => {
        panel.hidden = panel.dataset.footerPanel !== name;
      });
      root.hidden = false;
      requestAnimationFrame(() => root.classList.add('is-open'));
      document.body.classList.add('no-scroll');
      if (drawer) setTimeout(() => drawer.focus(), 60);
    };

    $$('[data-footer-panel-open]').forEach(btn => {
      btn.addEventListener('click', () => open(btn.dataset.footerPanelOpen, btn));
    });
    $$('[data-footer-panel-close]', root).forEach(btn => btn.addEventListener('click', close));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !root.hidden) close();
    });
    if (drawer) trapFocus(drawer);
  }
};

/* ── Header scrim ───────────────────────────────────────────── */
const Header = {
  init() {
    const header = $('.js-site-header');
    const hero = $('.js-hero');
    if (!header) return;
    const update = () => {
      if (!hero) return;
      const threshold = hero.offsetHeight - 80;
      const pastHero = window.scrollY >= threshold;
      header.classList.toggle('site-header--scrim', !pastHero);
    };
    let raf = null;
    window.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; update(); });
    }, { passive: true });
    update();
  }
};

const Overlays = {
  lastMenuTrigger: null,
  activateMenuGroup(btn) {
    if (!btn) return;
    const group = btn.dataset.group;
    $$('.js-menu-expand').forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-expanded', 'false'); });
    $$('.site-menu__group').forEach(g => { g.hidden = true; g.setAttribute('aria-hidden', 'true'); });
    btn.classList.add('is-active');
    btn.setAttribute('aria-expanded', 'true');
    const groupEl = $(`#menu-group-${group}`);
    if (groupEl) { groupEl.hidden = false; groupEl.setAttribute('aria-hidden', 'false'); }
    const content = $('.site-menu__content');
    if (content) content.classList.add('has-active');
  },
  closeMenuContent() {
    $$('.js-menu-expand').forEach(b => {
      b.classList.remove('is-active');
      b.setAttribute('aria-expanded', 'false');
    });
    $$('.site-menu__group').forEach(g => {
      g.hidden = true;
      g.setAttribute('aria-hidden', 'true');
    });
    const content = $('.site-menu__content');
    if (content) content.classList.remove('has-active');
  },
  openMenu() {
    const el = $('#site-menu');
    if (!el) return;
    this.lastMenuTrigger = document.activeElement?.matches?.('[aria-controls="site-menu"]')
      ? document.activeElement
      : document.querySelector('[aria-controls="site-menu"]');
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll', 'menu-is-open');
    const close = el.querySelector('.js-menu-close');
    if (close) setTimeout(() => close.focus(), 50);
    trapFocus(el);
    $$('[aria-controls="site-menu"]').forEach(b => b.setAttribute('aria-expanded', 'true'));
  },
  closeMenu(restoreFocus = true) {
    const el = $('#site-menu');
    if (!el) return;
    const wasOpen = el.classList.contains('is-open');
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll', 'menu-is-open');
    $$('[aria-controls="site-menu"]').forEach(b => b.setAttribute('aria-expanded', 'false'));
    this.closeMenuContent();
    if (restoreFocus && wasOpen && this.lastMenuTrigger) {
      setTimeout(() => this.lastMenuTrigger?.focus?.(), 0);
    }
  },
  openSearch() {
    const el = $('#search-overlay');
    if (!el) return;
    this.closeMenu(false);
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    const inp = el.querySelector('.js-search-input');
    if (inp) setTimeout(() => inp.focus(), 100);
    trapFocus(el);
    $$('[aria-controls="search-overlay"]').forEach(b => b.setAttribute('aria-expanded', 'true'));
  },
  closeSearch() {
    const el = $('#search-overlay');
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    $$('[aria-controls="search-overlay"]').forEach(b => b.setAttribute('aria-expanded', 'false'));
  },
  openCart() {
    const el = $('#cart-overlay');
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    Cart.refresh();
    trapFocus(el);
    $$('[aria-controls="cart-drawer"]').forEach(b => b.setAttribute('aria-expanded', 'true'));
  },
  closeCart() {
    const el = $('#cart-overlay');
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    $$('[aria-controls="cart-drawer"]').forEach(b => b.setAttribute('aria-expanded', 'false'));
  },
  init() {
    $$('.js-open-menu').forEach(b => b.addEventListener('click', () => this.openMenu()));
    $$('.js-open-search, .js-open-search-from-menu').forEach(b => b.addEventListener('click', () => this.openSearch()));
    $$('.js-open-cart').forEach(b => b.addEventListener('click', () => this.openCart()));

    $$('.js-menu-expand').forEach(btn => {
      btn.addEventListener('click', () => {
        const isOpen = btn.classList.contains('is-active');
        if (isOpen) {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-expanded', 'false');
          const groupEl = $(`#menu-group-${btn.dataset.group}`);
          if (groupEl) { groupEl.hidden = true; groupEl.setAttribute('aria-hidden', 'true'); }
          const content = $('.site-menu__content');
          const anyOpen = $$('.js-menu-expand').some(b => b.classList.contains('is-active'));
          if (content && !anyOpen) content.classList.remove('has-active');
          return;
        }
        Overlays.activateMenuGroup(btn);
      });
    });

    $$('.js-menu-close').forEach(b => b.addEventListener('click', () => this.closeMenu()));
    $$('.js-menu-content-close').forEach(b => b.addEventListener('click', () => this.closeMenuContent()));
    $$('.js-menu-overlay').forEach(el => {
      el.addEventListener('click', event => {
        if (event.target === el) this.closeMenu();
      });
    });
    $$('.site-menu__sub-link').forEach(link => link.addEventListener('click', () => this.closeMenu()));
    $$('.js-close-search, .js-search-overlay .js-cart-close').forEach(b => b.addEventListener('click', () => this.closeSearch()));
    $$('.js-search-overlay').forEach(el => {
      el.addEventListener('click', event => {
        if (event.target === el) this.closeSearch();
      });
    });
    $$('.js-cart-close').forEach(b => b.addEventListener('click', () => this.closeCart()));
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      this.closeMenu();
      this.closeSearch();
      this.closeCart();
    });
  }
};

/* ── Cart ───────────────────────────────────────────────────── */
const Cart = {
  freeShippingThreshold: window.__chess?.freeShippingThreshold || 15000,

  async fetch() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async add(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) throw new Error('Could not add to cart');
    return res.json();
  },

  async update(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return res.json();
  },

  async change(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    return res.json();
  },

  updateCountBadges(count) {
    $$('.js-cart-count').forEach(el => { el.textContent = count > 0 ? count : ''; });
    $$('.js-cart-count-word').forEach(el => { el.textContent = count > 0 ? ` (${count})` : ''; });
    $$('.js-cart-total-qty').forEach(el => { el.textContent = `(${count})`; });
  },

  renderShippingBar(cart) {
    const wrap = $('.js-shipping-bar-wrap');
    const text = $('.js-shipping-bar-text');
    const fill = $('.js-shipping-bar-fill');
    if (!wrap || !text || !fill) return;
    const price = cart.total_price;
    const currency = cart.currency;
    const threshold = this.freeShippingThreshold;
    const progress = Math.min(1, price / threshold);
    const remaining = Math.max(0, threshold - price);
    fill.style.width = `${progress * 100}%`;
    if (price >= threshold) {
      text.innerHTML = '<span style="color:var(--bone);">COMPLIMENTARY SHIPPING UNLOCKED</span>';
    } else {
      text.textContent = `${money(remaining, currency)} TO COMPLIMENTARY SHIPPING`;
    }
    wrap.style.display = cart.item_count > 0 ? '' : 'none';
  },

  renderItems(cart) {
    const listEl = $('.js-cart-item-list');
    const emptyEl = $('.js-cart-empty');
    const footerEl = $('.js-cart-footer');
    const template = $('#cart-item-template');
    if (!listEl || !template) return;
    if (cart.item_count === 0) {
      listEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = '';
      if (footerEl) footerEl.style.display = 'none';
      return;
    }
    if (emptyEl) emptyEl.style.display = 'none';
    if (footerEl) footerEl.style.display = '';
    listEl.style.display = '';
    listEl.innerHTML = '';
    cart.items.forEach((item, index) => {
      const clone = template.content.cloneNode(true);
      const li = clone.querySelector('li');
      li.dataset.lineIndex = index + 1;
      const img = clone.querySelector('img');
      if (item.image) { img.src = item.image.replace(/(\.\w+)$/, '_120x144$1'); img.alt = item.title; }
      else { img.parentElement.style.display = 'none'; }
      clone.querySelector('.cart-item__title').textContent = item.product_title;
      clone.querySelector('.cart-item__variant').textContent =
        item.variant_title !== 'Default Title' ? item.variant_title : '';
      clone.querySelector('.js-qty-value').textContent = item.quantity;
      clone.querySelector('.js-item-price').textContent = money(item.line_price, cart.currency);
      clone.querySelector('.js-qty-minus').addEventListener('click', async () => {
        await this.change(index + 1, item.quantity - 1);
        this.refresh();
      });
      clone.querySelector('.js-qty-plus').addEventListener('click', async () => {
        await this.change(index + 1, item.quantity + 1);
        this.refresh();
      });
      clone.querySelector('.js-remove-item').addEventListener('click', async () => {
        await this.change(index + 1, 0);
        this.refresh();
      });
      listEl.appendChild(clone);
    });
    const totalEl = $('.js-cart-total-price');
    if (totalEl) totalEl.textContent = money(cart.total_price, cart.currency);
  },

  async refresh() {
    try {
      const cart = await this.fetch();
      this.updateCountBadges(cart.item_count);
      this.renderShippingBar(cart);
      this.renderItems(cart);
    } catch (e) {
      console.error('[Cart] refresh failed:', e);
    }
  },

  async addAndOpen(variantId, quantity = 1) {
    try {
      await this.add(variantId, quantity);
      await this.refresh();
      Overlays.openCart();
    } catch (e) {
      console.error('[Cart] add failed:', e);
      alert('Could not add to cart. Please try again.');
    }
  },

  init() { this.refresh(); }
};/* ── Product gallery ────────────────────────────────────────── */
const Gallery = {
  products: [],
  sort: 'featured',
  filters: { inStock: false, sizes: [], priceMax: null },
  campaignVideoUrl: null,

  SIZE_ORDER: ['XXS','XS','S','M','L','XL','XXL'],

  async load() {
    const section = $('.product-gallery');
    if (!section) return;
    this.campaignVideoUrl = section.dataset.campaignVideo || null;
    const loadingEl = section.querySelector('.js-gallery-loading');
    const errorEl   = section.querySelector('.js-gallery-error');
    const emptyEl   = section.querySelector('.js-gallery-empty');
    const dataEl    = section.querySelector('[data-gallery-products]');
    if (dataEl && dataEl.textContent.trim()) {
      try {
        this.products = JSON.parse(dataEl.textContent) || [];
        this.buildFilters();
        this.render();
        return;
      } catch (e) {
        console.warn('[Gallery] embedded products failed:', e);
      }
    }
    if (loadingEl) loadingEl.style.display = 'flex';
    try {
      const res = await fetch('/products.json?limit=250');
      const json = await res.json();
      this.products = json.products || [];
      if (loadingEl) loadingEl.style.display = 'none';
      this.buildFilters();
      this.render();
    } catch (e) {
      console.error('[Gallery] load failed:', e);
      if (loadingEl) loadingEl.style.display = 'none';
      const hasRenderedCards = section.querySelectorAll('.product-card').length > 0;
      if (errorEl && !hasRenderedCards) errorEl.style.display = 'block';
    }
  },

  minPrice(p) { return Math.min(...p.variants.map(v => v.price)); },

  optionName(option) {
    return typeof option === 'string' ? option : option?.name || '';
  },

  optionIndex(product, names) {
    const wanted = names.map(n => n.toLowerCase());
    return (product.options || []).findIndex(option => wanted.includes(this.optionName(option).toLowerCase()));
  },

  sizes(p) {
    const idx = this.optionIndex(p, ['size']);
    if (idx < 0) return [];
    const vals = new Set();
    p.variants.forEach(v => { if (v[`option${idx+1}`]) vals.add(v[`option${idx+1}`].toUpperCase()); });
    return [...vals];
  },

  swatchColor(value = '') {
    const key = value.toLowerCase().trim();
    const map = {
      black: '#0a0a0a',
      charcoal: '#2a2a2a',
      'charcoal grey': '#3c3c3c',
      grey: '#8c8b86',
      gray: '#8c8b86',
      'blue grey': '#7f8d98',
      navy: '#0f1a2b',
      white: '#f4f1ea',
      bone: '#e6e0d4',
      cream: '#efe7d6',
      stone: '#a59c8e',
      taupe: '#7a6f60',
      plum: '#3a2630'
    };
    return map[key] || key.replace(/\s+/g, '');
  },

  colorVariants(product, index) {
    const idx = this.optionIndex(product, ['color', 'colour']);
    if (idx < 0) return [];
    const seen = new Map();
    (product.variants || []).forEach(variant => {
      const value = variant[`option${idx + 1}`];
      const available = this.isVariantAvailable(product, variant);
      if (!value) return;
      if (seen.has(value) && !available) return;
      seen.set(value, {
        value,
        variantId: variant.id,
        available,
        image: variant.featured_image?.src || variant.featured_image || product.images?.[0]?.src || ''
      });
    });
    return [...seen.values()].map((color, i) => `
      <button
        type="button"
        class="product-card__swatch js-card-color${i === 0 ? ' is-selected' : ''}"
        data-product-idx="${index}"
        data-variant-id="${color.variantId}"
        data-image="${color.image || ''}"
        style="--swatch:${this.swatchColor(color.value)};"
        aria-label="Select ${escapeHTML(color.value)}"
        title="${escapeHTML(color.value)}"
        ${!color.available ? 'aria-disabled="true"' : ''}
      ><span>${escapeHTML(color.value)}</span></button>
    `);
  },

  firstAvailableVariant(product) {
    return (product.variants || []).find(variant => this.isVariantAvailable(product, variant)) || (product.variants || [])[0] || null;
  },

  isProductAvailable(product) {
    if (product?.available === true || product?.available === 'true') return true;
    return (product?.variants || []).some(variant => this.isVariantAvailable(product, variant));
  },

  isVariantAvailable(product, variant) {
    if (!variant) return false;
    if (product?.available === true || product?.available === 'true') return true;
    if (variant.available === true || variant.available === 'true') return true;
    if (Number(variant.inventory_quantity) > 0) return true;
    if (variant.inventory_policy === 'continue') return true;
    if (variant.available === false || variant.available === 'false') return false;
    return product?.available === true || product?.available === 'true';
  },

  sizeVariants(product, sizeIdx) {
    if (sizeIdx < 0) return [];
    const seen = new Map();
    (product.variants || []).forEach(variant => {
      const size = variant[`option${sizeIdx + 1}`];
      const available = this.isVariantAvailable(product, variant);
      if (!size) return;
      const current = seen.get(size);
      if (!current || (!current.available && available)) {
        seen.set(size, { id: variant.id, size, available });
      }
    });
    return [...seen.values()];
  },

  displayTitle(title = '') {
    return String(title)
      .replace(/^chess\s+/i, '')
      .replace(/^first position\s+/i, '');
  },

  buildFilters() {
    const section = $('.product-gallery');
    if (!section) return;
    const allSizes = new Set();
    let lo = Infinity, hi = 0;
    this.products.forEach(p => {
      this.sizes(p).forEach(s => allSizes.add(s));
      const price = this.minPrice(p);
      if (price < lo) lo = price;
      if (price > hi) hi = price;
    });
    const sortedSizes = [...allSizes].sort((a,b) => {
      const ia = this.SIZE_ORDER.indexOf(a), ib = this.SIZE_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1; if (ib === -1) return -1;
      return ia - ib;
    });
    const sizeWrap = section.querySelector('.js-size-buttons');
    const sizeBlock = $('#filter-sizes');
    if (sizeWrap && sortedSizes.length > 0) {
      if (sizeBlock) sizeBlock.style.display = '';
      sortedSizes.forEach(s => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'size-btn';
        btn.textContent = s;
        btn.dataset.size = s;
        btn.addEventListener('click', () => {
          const active = this.filters.sizes.includes(s);
          this.filters.sizes = active ? this.filters.sizes.filter(x => x !== s) : [...this.filters.sizes, s];
          btn.classList.toggle('is-selected', !active);
          this.updateFilterCount();
          this.render();
        });
        sizeWrap.appendChild(btn);
      });
    }
    const priceRange = section.querySelector('.js-price-range');
    const priceLabel = section.querySelector('.js-price-label');
    const priceBlock = $('#filter-price');
    if (priceRange && hi > lo) {
      if (priceBlock) priceBlock.style.display = '';
      priceRange.min = String(lo);
      priceRange.max = String(hi);
      priceRange.value = String(hi);
      this.filters.priceMax = null;
      priceRange.addEventListener('input', () => {
        const val = Number(priceRange.value);
        this.filters.priceMax = val < hi ? val : null;
        if (priceLabel) priceLabel.textContent = money(val * 100);
        this.updateFilterCount();
        this.render();
      });
      if (priceLabel) priceLabel.textContent = money(hi * 100);
    }
    const stockCheck = section.querySelector('.js-filter-stock');
    if (stockCheck) {
      stockCheck.addEventListener('change', () => {
        this.filters.inStock = stockCheck.checked;
        this.updateFilterCount();
        this.render();
      });
    }
    const clearBtn = section.querySelector('.js-filter-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.filters = { inStock: false, sizes: [], priceMax: null };
        if (stockCheck) stockCheck.checked = false;
        $$('.size-btn', section).forEach(b => b.classList.remove('is-selected'));
        if (priceRange && hi > lo) {
          priceRange.value = String(hi);
          if (priceLabel) priceLabel.textContent = money(hi * 100);
        }
        this.updateFilterCount();
        this.render();
      });
    }
    $$('.js-sort-btn', section).forEach(btn => {
      btn.addEventListener('click', () => {
        this.sort = btn.dataset.sort;
        $$('.js-sort-btn', section).forEach(b => {
          const active = b.dataset.sort === this.sort;
          b.setAttribute('aria-selected', active ? 'true' : 'false');
          b.style.color = active ? 'var(--bone)' : 'rgba(232,228,221,0.5)';
        });
        $('#sort-panel')?.classList.remove('is-open');
        this.render();
      });
    });
    const filterToggle = section.querySelector('.js-filter-toggle');
    const filterPanel  = $('#filter-panel');
    const sortToggle   = section.querySelector('.js-sort-toggle');
    const sortPanel    = $('#sort-panel');
    filterToggle?.addEventListener('click', () => {
      const open = filterPanel?.classList.toggle('is-open');
      filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      sortPanel?.classList.remove('is-open');
    });
    sortToggle?.addEventListener('click', () => {
      const open = sortPanel?.classList.toggle('is-open');
      sortToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      filterPanel?.classList.remove('is-open');
    });
    document.addEventListener('click', e => {
      if (!e.target.closest('.filter-bar')) {
        filterPanel?.classList.remove('is-open');
        sortPanel?.classList.remove('is-open');
      }
    });
  },

  updateFilterCount() {
    const count = (this.filters.inStock ? 1 : 0) + this.filters.sizes.length + (this.filters.priceMax !== null ? 1 : 0);
    const el = document.querySelector('.js-filter-count');
    if (el) el.textContent = count > 0 ? ` [${count}]` : '';
    const clearBtn = document.querySelector('.js-filter-clear');
    if (clearBtn) clearBtn.style.display = count > 0 ? 'inline' : 'none';
  },

  bindSectionObserver(section) {
    if (!('IntersectionObserver' in window) || section.dataset.headerObserver === 'true') return;
    section.dataset.headerObserver = 'true';
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        document.body.classList.toggle('product-gallery-in-view', entry.isIntersecting);
      });
    }, { rootMargin: '-80px 0px -55% 0px', threshold: 0 });
    observer.observe(section);
  },

  filtered() {
    let list = [...this.products];
    if (this.filters.inStock) list = list.filter(p => this.isProductAvailable(p));
    if (this.filters.sizes.length > 0) list = list.filter(p => this.filters.sizes.some(s => this.sizes(p).includes(s)));
    if (this.filters.priceMax !== null) list = list.filter(p => this.minPrice(p) <= this.filters.priceMax);
    switch (this.sort) {
      case 'price-asc':  list.sort((a,b) => this.minPrice(a) - this.minPrice(b)); break;
      case 'price-desc': list.sort((a,b) => this.minPrice(b) - this.minPrice(a)); break;
      case 'az':         list.sort((a,b) => a.title.localeCompare(b.title)); break;
      case 'newest':     list.reverse(); break;
    }
    return list;
  },

  productCardHTML(product, index) {
    const images = product.images || [];
    const img1 = images[0];
    const img2 = images[1];
    const variants = product.variants || [];
    const firstVariant = this.firstAvailableVariant(product);
    const firstVariantAvailable = this.isProductAvailable(product);
    const price = firstVariant ? productMoney(firstVariant.price) : '';
    const displayTitle = this.displayTitle(product.title);
    const sizeIdx = this.optionIndex(product, ['size']);
    const sizeVariants = this.sizeVariants(product, sizeIdx);
    const hasRealVariants = variants.length > 1 || (firstVariant && firstVariant.title !== 'Default Title');
    const activeSize = sizeIdx >= 0 && firstVariant ? firstVariant[`option${sizeIdx + 1}`] : null;
    const colorHTML = this.colorVariants(product, index).join('');
    const sizeBtnsHTML = (hasRealVariants && sizeIdx >= 0 && sizeVariants.length > 0)
      ? `<div class="product-card__sizes">
          ${sizeVariants.map((v,i) => `
            <button type="button"
              class="size-btn js-card-size${(activeSize && v.size === activeSize) || (!activeSize && i === 0) ? ' is-selected' : ''}${!v.available ? ' is-unavailable' : ''}"
              data-variant-id="${v.id}"
              data-product-idx="${index}"
              ${!v.available ? 'aria-disabled="true"' : ''}
            >${v.size ? v.size.toUpperCase() : v.id}</button>
          `).join('')}
        </div>`
      : '';
    const saved = Wishlist.has(product.handle);
    const imageDots = images.slice(0, 4).map((_, i) => `<button type="button" class="product-card__dot js-card-image-dot${i === 0 ? ' is-active' : ''}" data-product-idx="${index}" data-image-index="${i}" aria-label="View product image ${i + 1}"></button>`).join('');
    return `
      <div class="product-card" data-product-idx="${index}">
        <div class="product-card__image-wrap">
          <a href="${product.url || `/products/${product.handle}`}" class="product-card__image-link js-card-image-link" aria-label="${escapeHTML(displayTitle)}">
            ${img1 ? `<img class="product-card__img product-card__img--primary js-card-primary-img" src="${img1.src.replace(/(\.\w+)$/, '_600x750$1')}" alt="${escapeHTML(img1.alt || displayTitle)}" loading="${index < 3 ? 'eager' : 'lazy'}" decoding="async">` : ''}
            ${img2 ? `<img class="product-card__img product-card__img--alt" src="${img2.src.replace(/(\.\w+)$/, '_600x750$1')}" alt="${escapeHTML(img2.alt || displayTitle)}" loading="lazy" decoding="async">` : ''}
          </a>
          ${images.length > 1 ? `
            <span class="product-card__swipe-hint" aria-hidden="true">SWIPE</span>
            <button type="button" class="product-card__image-nav product-card__image-nav--prev js-card-image-prev" data-product-idx="${index}" aria-label="Previous product image">‹</button>
            <button type="button" class="product-card__image-nav product-card__image-nav--next js-card-image-next" data-product-idx="${index}" aria-label="Next product image">›</button>
            <div class="product-card__dots">${imageDots}</div>
          ` : ''}
        </div>
        <div class="product-card__info">
          <div class="product-card__row1">
            <span class="product-card__name">${escapeHTML(displayTitle)}</span>
            <span class="product-card__price js-card-price">${price}</span>
          </div>
          ${colorHTML ? `<div class="product-card__swatches" aria-label="Colour options">${colorHTML}</div>` : ''}
          ${sizeBtnsHTML}
          <div class="product-card__actions">
            <button type="button" class="quick-buy-btn js-quick-buy" data-product-idx="${index}" ${!firstVariantAvailable ? 'disabled' : ''}>
              ${!firstVariantAvailable ? 'SOLD OUT' : 'QUICK BUY +'}
            </button>
            <button type="button" class="wishlist-btn js-wishlist-card${saved ? ' is-saved' : ''}" data-handle="${product.handle}" data-product-idx="${index}">
              ${saved ? 'WISHLIST ✓' : 'WISHLIST +'}
            </button>
          </div>
        </div>
      </div>
    `;
  },

  campaignBlockHTML(index) {
    const label = index === 0 ? 'CAMPAIGN — SS26' : `CHAPTER ${String(index + 1).padStart(2,'0')}`;
    const videoSrc = this.campaignVideoUrl;
    return `
      <div class="campaign-block">
        <div class="campaign-block__meta">
          <p class="micro-sm" style="color:rgba(232,228,221,0.6);">${label}</p>
          <p class="micro-sm" style="color:rgba(232,228,221,0.4);">${String(index+1).padStart(2,'0')}</p>
        </div>
        <div class="campaign-block__video-wrap">
          ${videoSrc ? `<video class="campaign-block__video" autoplay muted loop playsinline disableremoteplayback preload="auto"><source src="${videoSrc}" type="video/mp4"></video>` : ''}
          <div class="campaign-block__overlay"></div>
          <div class="campaign-block__footer">
            <p class="micro-sm" style="color:var(--bone);">CHESS</p>
            <a href="#shop" class="micro-sm" style="color:rgba(232,228,221,0.8);">SHOP →</a>
          </div>
        </div>
      </div>
    `;
  },

  render() {
    const section = $('.product-gallery');
    if (!section) return;
    const grid   = section.querySelector('.js-product-grid');
    const mobile = section.querySelector('.js-mobile-feed');
    const empty  = section.querySelector('.js-gallery-empty');
    const countEl = document.querySelector('[data-product-count]');
    this.bindSectionObserver(section);
    const list = this.filtered();
    if (countEl) countEl.textContent = list.length ? String(list.length).padStart(2,'0') : '';
    if (list.length === 0) {
      if (grid)   grid.innerHTML = '';
      if (mobile) mobile.innerHTML = '';
      if (empty)  empty.style.display = 'block';
      return;
    }
    if (empty) empty.style.display = 'none';
    if (grid) grid.innerHTML = list.map((p, i) => this.productCardHTML(p, i)).join('');
    if (mobile) {
      const clusters = [];
      for (let i = 0; i < list.length; i += 4) clusters.push(list.slice(i, i+4));
      mobile.innerHTML = clusters.map((cluster, ci) => `
        ${this.campaignBlockHTML(ci)}
        <div class="mobile-grid">
          ${cluster.map((p, i) => this.productCardHTML(p, ci*4+i)).join('')}
        </div>
      `).join('');
    }
    this.bindCardEvents(section);
  },

  bindCardEvents(section) {
    const setCardImage = (card, product, index) => {
      const images = product.images || [];
      if (!images.length) return;
      card.classList.add('has-swiped');
      const bounded = (index + images.length) % images.length;
      card.dataset.imageIndex = String(bounded);
      const image = images[bounded];
      const img = card.querySelector('.js-card-primary-img');
      if (img && image?.src) {
        img.src = image.src.replace(/(\.\w+)$/, '_600x750$1');
        img.alt = image.alt || product.title;
      }
      $$('.js-card-image-dot', card).forEach(dot => {
        dot.classList.toggle('is-active', parseInt(dot.dataset.imageIndex, 10) === bounded);
      });
    };

    $$('.js-card-image-prev, .js-card-image-next, .js-card-image-dot', section).forEach(btn => {
      btn.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        const card = btn.closest('.product-card');
        const idx = parseInt(btn.dataset.productIdx, 10);
        const product = this.filtered()[idx];
        if (!card || !product) return;
        const current = parseInt(card.dataset.imageIndex || '0', 10);
        const next = btn.classList.contains('js-card-image-dot')
          ? parseInt(btn.dataset.imageIndex, 10)
          : current + (btn.classList.contains('js-card-image-prev') ? -1 : 1);
        setCardImage(card, product, next);
      });
    });

    $$('.js-card-image-link', section).forEach(link => {
      let startX = 0;
      let swiping = false;
      link.addEventListener('touchstart', event => {
        startX = event.touches[0]?.clientX || 0;
        swiping = false;
      }, { passive: true });
      link.addEventListener('touchmove', event => {
        const dx = (event.touches[0]?.clientX || 0) - startX;
        if (Math.abs(dx) > 28) swiping = true;
      }, { passive: true });
      link.addEventListener('touchend', event => {
        if (!swiping) return;
        event.preventDefault();
        const card = link.closest('.product-card');
        const idx = parseInt(card?.dataset.productIdx || '', 10);
        const product = this.filtered()[idx];
        if (!card || !product) return;
        const current = parseInt(card.dataset.imageIndex || '0', 10);
        const endX = event.changedTouches[0]?.clientX || startX;
        setCardImage(card, product, current + (endX < startX ? 1 : -1));
      });
    });

    $$('.js-card-color', section).forEach(btn => {
      btn.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        const card = btn.closest('.product-card');
        const idx = parseInt(btn.dataset.productIdx, 10);
        const product = this.filtered()[idx];
        if (!card || !product) return;
        $$('.js-card-color', card).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        const variantId = parseInt(btn.dataset.variantId, 10);
        const variant = product.variants.find(v => v.id === variantId);
        if (variant) {
          const available = this.isProductAvailable(product) || this.isVariantAvailable(product, variant);
          const image = variant.featured_image?.src || variant.featured_image || btn.dataset.image;
          const img = card.querySelector('.js-card-primary-img');
          if (img && image) img.src = String(image).replace(/(\.\w+)$/, '_600x750$1');
          const priceEl = card.querySelector('.js-card-price');
          if (priceEl) priceEl.textContent = productMoney(variant.price);
          const buyPriceEl = card.querySelector('.js-card-buy-price');
          if (buyPriceEl) buyPriceEl.textContent = productMoney(variant.price);
          $$('.js-quick-buy', card).forEach(qbBtn => {
            qbBtn.disabled = !available;
            qbBtn.dataset.variantOverride = variantId;
            const label = qbBtn.querySelector('span:first-child');
            if (label) label.textContent = available ? 'BUY NOW' : 'SOLD OUT';
            else qbBtn.textContent = available ? 'QUICK BUY +' : 'SOLD OUT';
          });
        }
      });
    });

    $$('.js-card-size', section).forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const card = btn.closest('.product-card');
        const idx = parseInt(btn.dataset.productIdx, 10);
        const product = this.filtered()[idx];
        if (!product) return;
        $$('.js-card-size', card).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        const variantId = parseInt(btn.dataset.variantId, 10);
        const variant = product.variants.find(v => v.id === variantId);
        if (variant) {
          const available = this.isProductAvailable(product) || this.isVariantAvailable(product, variant);
          const priceEl = card.querySelector('.js-card-price');
          if (priceEl) priceEl.textContent = productMoney(variant.price);
          const buyPriceEl = card.querySelector('.js-card-buy-price');
          if (buyPriceEl) buyPriceEl.textContent = productMoney(variant.price);
          const qbBtn = card.querySelector('.js-quick-buy');
          if (qbBtn) {
            qbBtn.disabled = !available;
            const label = qbBtn.querySelector('span:first-child');
            if (label) label.textContent = available ? 'BUY NOW' : 'SOLD OUT';
            else qbBtn.textContent = available ? 'QUICK BUY +' : 'SOLD OUT';
            qbBtn.dataset.variantOverride = variantId;
          }
          $$('.js-quick-buy', card).forEach(button => {
            button.disabled = !available;
            button.dataset.variantOverride = variantId;
            if (!button.querySelector('span:first-child')) button.textContent = available ? 'QUICK BUY +' : 'SOLD OUT';
          });
        }
      });
    });
    $$('.js-quick-buy', section).forEach(btn => {
      btn.addEventListener('click', async () => {
        const idx = parseInt(btn.dataset.productIdx, 10);
        const product = this.filtered()[idx];
        if (!product) return;
        const fallbackVariant = this.firstAvailableVariant(product);
        const resolvedVariantId = btn.dataset.variantOverride ? parseInt(btn.dataset.variantOverride, 10) : fallbackVariant?.id;
        if (!resolvedVariantId) return;
        const variant = product.variants.find(v => v.id === resolvedVariantId);
        const available = this.isProductAvailable(product) || this.isVariantAvailable(product, variant);
        if (!available) return;
        const label = btn.querySelector('span:first-child');
        const oldLabel = label ? label.textContent : btn.textContent;
        if (label) label.textContent = 'ADDING';
        else btn.textContent = '…';
        btn.disabled = true;
        try { await Cart.addAndOpen(resolvedVariantId, 1); }
        finally {
          if (label) label.textContent = oldLabel;
          else btn.textContent = 'QUICK BUY +';
          btn.disabled = !available;
        }
      });
    });
    $$('.js-wishlist-card', section).forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.handle;
        const idx = parseInt(btn.dataset.productIdx, 10);
        const product = this.filtered()[idx];
        if (!product) return;
        const saved = Wishlist.toggle(handle, product);
        btn.classList.toggle('is-saved', saved);
        btn.textContent = saved ? 'WISHLIST ✓' : 'WISHLIST +';
      });
    });
  },

  init() {
    if (!$('.product-gallery')) return;
    this.load();
  }
};

/* ── Wishlist ────────────────────────────────────────────────── */
const Wishlist = {
  key: 'chess-wishlist',
  get() {
    try { return JSON.parse(localStorage.getItem(this.key) || '{}'); }
    catch { return {}; }
  },
  has(handle) { return !!this.get()[handle]; },
  updateCount() {
    const count = Object.keys(this.get()).length;
    $$('.js-wishlist-count').forEach(el => { el.textContent = count; });
  },
  image(product) {
    const image = product?.images?.[0] || product?.featured_image || null;
    if (typeof image === 'string') return image;
    return image?.src || null;
  },
  toggle(handle, product) {
    const w = this.get();
    if (w[handle]) { delete w[handle]; }
    else {
      w[handle] = {
        handle,
        title: product?.title || handle,
        image: this.image(product),
        url: product?.url || `/products/${handle}`
      };
    }
    localStorage.setItem(this.key, JSON.stringify(w));
    this.updateCount();
    document.dispatchEvent(new CustomEvent('chess:wishlist-change', { detail: { handle, saved: !!w[handle] } }));
    return !!w[handle];
  },
  remove(handle) {
    const w = this.get();
    delete w[handle];
    localStorage.setItem(this.key, JSON.stringify(w));
    this.updateCount();
    document.dispatchEvent(new CustomEvent('chess:wishlist-change', { detail: { handle, saved: false } }));
  },
  init() {
    this.updateCount();
  }
};

const WishlistPage = {
  root: null,
  labels: {},

  async loadProduct(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`Product ${handle} could not be loaded`);
    return response.json();
  },

  productImage(product, saved) {
    const image = product?.featured_image || product?.images?.[0] || saved?.image || '';
    if (typeof image === 'string') return image;
    return image?.src || '';
  },

  card(product, saved) {
    const handle = product?.handle || saved.handle;
    const title = product?.title || saved.title || handle;
    const url = product?.url || saved.url || `/products/${handle}`;
    const image = this.productImage(product, saved);
    const variants = product?.variants || [];
    const availableVariants = variants.filter(variant => variant.available);
    const defaultVariant = availableVariants[0] || null;
    const hasChoices = variants.length > 1 || (variants[0] && variants[0].title !== 'Default Title');
    const price = product ? money(product.price_min ?? product.price ?? defaultVariant?.price ?? variants[0]?.price ?? 0) : '';
    const controlId = `WishlistVariant-${handle.replace(/[^a-z0-9-]/gi, '-')}`;
    const choices = variants.map(variant => `
      <button
        type="button"
        class="wishlist-size${variant.id === defaultVariant?.id ? ' is-selected' : ''}"
        data-variant-id="${variant.id}"
        aria-pressed="${variant.id === defaultVariant?.id ? 'true' : 'false'}"
        ${variant.available ? '' : 'disabled'}
      >
        <span>${escapeHTML(variant.title)}</span>
        ${variant.available ? '' : `<small>${escapeHTML(this.labels.soldOut)}</small>`}
      </button>
    `).join('');
    const unavailable = product && availableVariants.length === 0;

    return `
      <article class="wishlist-card" data-wishlist-handle="${escapeHTML(handle)}">
        <a class="wishlist-card__media" href="${escapeHTML(url)}">
          ${image ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(title)}" loading="lazy" decoding="async">` : '<span></span>'}
        </a>
        <div class="wishlist-card__details">
          <div class="wishlist-card__heading">
            <a href="${escapeHTML(url)}">${escapeHTML(title)}</a>
            ${price ? `<span>${price}</span>` : ''}
          </div>
          ${hasChoices && !unavailable ? `
            <span class="wishlist-card__size-label" id="${controlId}">${escapeHTML(this.labels.chooseSize)}</span>
            <div class="wishlist-card__sizes js-wishlist-variants" role="group" aria-labelledby="${controlId}">${choices}</div>
          ` : ''}
          <div class="wishlist-card__actions">
            <button type="button" class="js-wishlist-add" data-default-variant="${defaultVariant?.id || ''}" ${unavailable || !product ? 'disabled' : ''}>
              ${escapeHTML(unavailable ? this.labels.soldOut : this.labels.addToCart)}
            </button>
            <button type="button" class="js-wishlist-remove">${escapeHTML(this.labels.remove)}</button>
          </div>
        </div>
      </article>
    `;
  },

  bind() {
    $$('.wishlist-card', this.root).forEach(card => {
      const handle = card.dataset.wishlistHandle;
      $$('.wishlist-size', card).forEach(sizeButton => {
        sizeButton.addEventListener('click', () => {
          $$('.wishlist-size', card).forEach(button => {
            const selected = button === sizeButton;
            button.classList.toggle('is-selected', selected);
            button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
        });
      });
      $('.js-wishlist-remove', card)?.addEventListener('click', () => {
        Wishlist.remove(handle);
        card.remove();
        if (!$('.wishlist-card', this.root)) this.showEmpty();
      });
      $('.js-wishlist-add', card)?.addEventListener('click', async event => {
        const button = event.currentTarget;
        const selectedSize = $('.wishlist-size.is-selected', card);
        const variantId = parseInt(selectedSize?.dataset.variantId || button.dataset.defaultVariant || '', 10);
        if (!variantId) return;
        const previous = button.textContent;
        button.disabled = true;
        button.textContent = this.labels.adding;
        try {
          await Cart.addAndOpen(variantId, 1);
        } catch (error) {
          button.textContent = this.labels.error;
          setTimeout(() => { button.textContent = previous; }, 1800);
        } finally {
          button.disabled = false;
          if (button.textContent === this.labels.adding) button.textContent = previous;
        }
      });
    });
  },

  showEmpty() {
    $('.js-wishlist-grid', this.root).innerHTML = '';
    $('.js-wishlist-empty', this.root).hidden = false;
    $('.js-wishlist-status', this.root).hidden = true;
  },

  async render() {
    const stored = Object.values(Wishlist.get()).reverse();
    if (!stored.length) {
      this.showEmpty();
      return;
    }

    const results = await Promise.all(stored.map(async item => {
      try { return { product: await this.loadProduct(item.handle), saved: item }; }
      catch { return { product: null, saved: item }; }
    }));

    const available = results.filter(item => item.product);
    const grid = $('.js-wishlist-grid', this.root);
    grid.innerHTML = available.map(item => this.card(item.product, item.saved)).join('');
    $('.js-wishlist-status', this.root).hidden = true;
    if (!available.length) {
      this.showEmpty();
      return;
    }
    this.bind();
  },

  init() {
    this.root = $('[data-wishlist-page]');
    if (!this.root) return;
    this.labels = {
      chooseSize: this.root.dataset.chooseSize,
      addToCart: this.root.dataset.addToCart,
      adding: this.root.dataset.adding,
      remove: this.root.dataset.remove,
      soldOut: this.root.dataset.soldOut,
      error: this.root.dataset.error
    };
    this.render();
  }
};

/* ── Statement scroll animation ─────────────────────────────── */
const Statement = {
  init() {
    const section = $('.js-statement');
    const inner   = $('.js-statement-text');
    if (!section || !inner) return;

    let raf = null;
    const update = () => {
      raf = null;
      const rect = section.getBoundingClientRect();
      const vh   = window.innerHeight || 1;

      if (document.body.classList.contains('a11y-reduce-motion')) {
        const visible = rect.top < vh * 0.9 && rect.bottom > 0;
        inner.style.opacity   = visible ? '1' : '0';
        inner.style.transform = 'translate3d(0,0,0) scale(1)';
        return;
      }

      const entry = Math.min(1, Math.max(0, (vh - rect.top) / (vh * 0.6)));
      const exit  = Math.min(1, Math.max(0, 1 - rect.bottom / (vh * 0.6)));

      inner.style.opacity   = String(Math.max(0, entry - exit));
      inner.style.transform = `translate3d(0, ${40*(1-entry) - 12*exit}px, 0) scale(${0.92 + 0.08*entry})`;
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }
};

/* ── Search ──────────────────────────────────────────────────── */
const Search = {
  debounceTimer: null,

  async query(terms) {
    const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(terms)}&resources[type]=product,collection,page&resources[limit]=6`);
    const json = await res.json();
    return json.resources?.results || {};
  },

  renderResults(results, container) {
    const products = results.products || [];
    const collections = results.collections || [];
    const pages = results.pages || [];
    if (!products.length && !collections.length && !pages.length) {
      container.innerHTML = '';
      return;
    }
    const productHTML = products.map(p => `
      <a href="${p.url}" class="search-result search-result--product">
        <div class="search-result__thumb">
          ${p.image ? `<img src="${p.image}" alt="${escapeHTML(p.title)}" loading="lazy">` : ''}
        </div>
        <div>
          <p>${escapeHTML(p.title)}</p>
          ${p.price ? `<span>${escapeHTML(p.price)}</span>` : ''}
        </div>
      </a>
    `).join('');
    const linkHTML = [...collections, ...pages].map(item => `
      <a href="${item.url}" class="search-result search-result--link">
        <p>${escapeHTML(item.title)}</p>
        <span>${collections.includes(item) ? 'COLLECTION' : 'PAGE'}</span>
      </a>
    `).join('');
    container.innerHTML = productHTML + linkHTML;
  },

  init() {
    const overlay = $('#search-overlay');
    if (!overlay) return;
    const input    = overlay.querySelector('.js-search-input');
    const results  = overlay.querySelector('.js-search-results');
    const status   = overlay.querySelector('.js-search-status');
    const closeBtn = overlay.querySelector('.js-close-search');
    if (closeBtn) closeBtn.addEventListener('click', () => Overlays.closeSearch());
    if (input && results) {
      input.addEventListener('input', () => {
        clearTimeout(this.debounceTimer);
        const q = input.value.trim();
        if (!q) { results.innerHTML = ''; if (status) status.style.display = 'none'; return; }
        this.debounceTimer = setTimeout(async () => {
          try {
            const found = await this.query(q);
            const total = (found.products || []).length + (found.collections || []).length + (found.pages || []).length;
            this.renderResults(found, results);
            if (status) {
              status.style.display = total === 0 ? '' : 'none';
              status.textContent   = total === 0 ? `NO RESULTS FOR "${q.toUpperCase()}"` : '';
            }
          } catch (e) { console.error('[Search] query failed:', e); }
        }, 280);
      });
    }
  }
};

/* ── Homepage scroll cursor ─────────────────────────────────── */
const ScrollCursor = {
  init() {
    const hero = $('.js-hero');
    const cursor = $('.js-scroll-cursor');
    if (!hero || !cursor || window.matchMedia('(pointer: coarse)').matches) return;

    let active = false;
    let raf = null;
    let x = 0;
    let y = 0;

    const move = () => {
      raf = null;
      cursor.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
    };

    hero.addEventListener('mouseenter', () => {
      active = true;
      document.body.classList.add('hero-cursor-active');
    });

    hero.addEventListener('mouseleave', () => {
      active = false;
      document.body.classList.remove('hero-cursor-active');
    });

    hero.addEventListener('mousemove', event => {
      if (!active) return;
      x = event.clientX;
      y = event.clientY;
      if (!raf) raf = requestAnimationFrame(move);
    }, { passive: true });

    window.addEventListener('scroll', () => {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
        active = false;
        document.body.classList.remove('hero-cursor-active');
      }
    }, { passive: true });
  }
};

/* ── Product Page ────────────────────────────────────────────── */
const ProductPage = {
  product: null,
  selectedOptions: [],
  selectedVariant: null,

  init() {
    const el = $('.product-page');
    if (!el) return;
    const dataEl = $('#product-json');
    if (!dataEl) return;
    this.product = JSON.parse(dataEl.textContent);
    const selectedId = parseInt($('#variant-id-input')?.value || '', 10);
    const initialVariant = this.product.variants.find(v => v.id === selectedId) || this.product.variants[0];
    this.selectedOptions = (this.product.options || []).map((_, i) => {
      const v = initialVariant;
      return v ? v[`option${i+1}`] : null;
    });
    this.selectedVariant = initialVariant;
    if (initialVariant) this.updateVariantUI(initialVariant);
    this.syncOptionButtons();
    this.bindOptions();
    this.bindForm();
    this.bindStickyATC();
    this.bindThumbs();
    this.bindGalleryStrip();
    this.bindAccordions();
    this.bindSizeGuide();
    FitFinder.init(this);
    this.bindWishlist();
  },

  findVariant() {
    return this.product.variants.find(v =>
      this.selectedOptions.every((val, i) => v[`option${i+1}`] === val)
    ) || null;
  },

  isVariantAvailable(variant) {
    if (!variant) return false;
    if (variant.available === true || variant.available === 'true') return true;
    if (Number(variant.inventory_quantity) > 0) return true;
    if (variant.inventory_policy === 'continue') return true;
    return false;
  },

  findCompatibleAvailableVariant(changedIndex = null, changedValue = null) {
    const variants = this.product.variants || [];
    return variants.find(variant => {
      if (changedIndex !== null && variant[`option${changedIndex + 1}`] !== changedValue) return false;
      if (!this.isVariantAvailable(variant)) return false;
      return this.selectedOptions.every((val, i) => {
        if (i === changedIndex) return true;
        return !val || variant[`option${i + 1}`] === val;
      });
    }) || variants.find(variant => {
      if (changedIndex !== null && variant[`option${changedIndex + 1}`] !== changedValue) return false;
      return this.isVariantAvailable(variant);
    }) || null;
  },

  syncSelectedOptionsFromVariant(variant) {
    if (!variant) return;
    this.selectedOptions = (this.product.options || []).map((_, i) => variant[`option${i + 1}`] || null);
  },

  syncOptionButtons() {
    $$('.js-option-btn').forEach(btn => {
      const idx = parseInt(btn.dataset.optionIndex, 10);
      const val = btn.dataset.optionValue;
      const active = this.selectedOptions[idx] === val;
      const valueAvailable = (this.product.variants || []).some(variant =>
        variant[`option${idx + 1}`] === val && this.isVariantAvailable(variant)
      );
      btn.classList.toggle('is-active', active);
      btn.classList.toggle('is-sold-out', !valueAvailable);
      if (valueAvailable) btn.removeAttribute('aria-disabled');
      else btn.setAttribute('aria-disabled', 'true');
    });
  },

  updateVariantUI(variant) {
    this.selectedVariant = variant;
    if (!variant) return;
    const isAvailable = this.isVariantAvailable(variant);
    const priceEl = $('#price-display');
    if (priceEl) priceEl.textContent = money(variant.price);
    const idInput = $('#variant-id-input');
    if (idInput) idInput.value = variant.id;
    const atcBtn = $('#atc-btn');
    if (atcBtn) {
      atcBtn.disabled = !isAvailable;
      atcBtn.textContent = isAvailable ? 'ADD TO CART' : 'SOLD OUT';
    }
    const notifyWrap = $('.js-notify-wrap');
    if (notifyWrap) notifyWrap.style.display = isAvailable ? 'none' : '';
    const stickyLabel = $('#sticky-variant-label');
    if (stickyLabel) stickyLabel.textContent = variant.title !== 'Default Title' ? variant.title : '';
    const stickyPrice = $('#sticky-price');
    if (stickyPrice) stickyPrice.textContent = money(variant.price);
    const stickyBtn = $('.js-sticky-atc-btn');
    if (stickyBtn) {
      stickyBtn.disabled = !isAvailable;
      stickyBtn.textContent = isAvailable ? 'ADD TO CART' : 'SOLD OUT';
    }
    const dynamicCheckout = $('.js-dynamic-checkout');
    if (dynamicCheckout) dynamicCheckout.hidden = !isAvailable;
    const colorLabel = $('.js-selected-color');
    if (colorLabel) {
      const colorIndex = (this.product.options || []).findIndex(o => ['color', 'colour'].includes(String(o).toLowerCase()));
      if (colorIndex >= 0) colorLabel.textContent = ` — ${variant[`option${colorIndex + 1}`].toUpperCase()}`;
    }
    const stockBadge = $('.js-stock-badge');
    if (stockBadge) {
      stockBadge.hidden = false;
      stockBadge.classList.toggle('product-stock-badge--muted', !isAvailable);
      if (!isAvailable) stockBadge.hidden = true;
      else if (variant.inventory_quantity > 0 && variant.inventory_quantity <= 2) stockBadge.textContent = `ONLY ${variant.inventory_quantity} LEFT`;
      else if (variant.inventory_quantity > 2 && variant.inventory_quantity <= 5) stockBadge.textContent = 'LOW STOCK';
      else stockBadge.hidden = true;
    }
    if (variant.featured_image) {
      const index = Math.max(0, (variant.featured_image.position || 1) - 1);
      this.setActiveThumb(index);
      const target = $$('.js-thumb')[index];
      target?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
  },

  bindOptions() {
    $$('.js-option-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const idx = parseInt(btn.dataset.optionIndex, 10);
        const val = btn.dataset.optionValue;
        this.selectedOptions[idx] = val;
        let variant = this.findVariant();
        if (!this.isVariantAvailable(variant)) {
          variant = this.findCompatibleAvailableVariant(idx, val);
          this.syncSelectedOptionsFromVariant(variant);
        }
        this.syncOptionButtons();
        this.updateVariantUI(variant || this.findVariant());
      });
    });
  },

  bindForm() {
    const form = $('#product-form');
    if (!form) return;
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const variant = this.selectedVariant;
      if (!variant || !this.isVariantAvailable(variant)) return;
      const btn = form.querySelector('.js-atc-btn');
      if (btn) { btn.disabled = true; btn.textContent = '…'; }
      try { await Cart.addAndOpen(variant.id, 1); }
      finally {
        const isAvailable = this.isVariantAvailable(variant);
        if (btn) { btn.disabled = !isAvailable; btn.textContent = isAvailable ? 'ADD TO CART' : 'SOLD OUT'; }
      }
    });
  },

  bindStickyATC() {
    const stickyEl = $('#sticky-atc');
    if (!stickyEl) return;
    stickyEl.classList.add('is-visible');
    stickyEl.setAttribute('aria-hidden', 'false');
    const stickyBtn = stickyEl.querySelector('.js-sticky-atc-btn');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', async () => {
        const v = this.selectedVariant;
        if (!v || !this.isVariantAvailable(v)) return;
        stickyBtn.disabled = true; stickyBtn.textContent = '…';
        try { await Cart.addAndOpen(v.id, 1); }
        finally {
          const isAvailable = this.isVariantAvailable(v);
          stickyBtn.disabled = !isAvailable;
          stickyBtn.textContent = isAvailable ? 'ADD TO CART' : 'SOLD OUT';
        }
      });
    }
  },

  bindThumbs() {
    $$('.js-thumb').forEach((btn, i) => {
      btn.addEventListener('click', () => {
        btn.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        this.setActiveThumb(i);
      });
    });
  },

  setActiveThumb(idx) {
    $$('.js-thumb').forEach((t, i) => t.classList.toggle('is-active', i === idx));
    const indexEl = $('.js-gallery-index');
    if (indexEl) indexEl.textContent = String(idx + 1).padStart(2, '0');
  },

  bindGalleryStrip() {
    const strip = $('.js-product-media-strip');
    if (!strip) return;
    let raf = null;
    const update = () => {
      raf = null;
      const items = $$('.js-thumb', strip);
      if (!items.length) return;
      const left = strip.scrollLeft;
      let closest = 0;
      let distance = Infinity;
      items.forEach((item, i) => {
        const d = Math.abs(item.offsetLeft - left);
        if (d < distance) { distance = d; closest = i; }
      });
      this.setActiveThumb(closest);
    };
    strip.addEventListener('scroll', () => {
      strip.classList.add('has-interacted');
      if (raf) return;
      raf = requestAnimationFrame(update);
    }, { passive: true });
    strip.addEventListener('pointerdown', () => strip.classList.add('has-interacted'), { passive: true });
  },

  bindAccordions() {
    $$('.js-accordion-item').forEach(item => {
      const trigger = item.querySelector('.js-accordion-trigger');
      if (!trigger) return;
      trigger.addEventListener('click', () => {
        const open = item.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  },

  bindSizeGuide() {
    const modal   = $('#size-guide-modal');
    const closeBtn = modal?.querySelector('.js-close-size-guide');
    if (!modal) return;
    const close = () => {
      modal.style.display = 'none';
      document.body.classList.remove('no-scroll');
    };
    $$('.js-open-size-guide').forEach(openBtn => {
      openBtn.addEventListener('click', () => { modal.style.display = ''; document.body.classList.add('no-scroll'); trapFocus(modal); });
    });
    closeBtn?.addEventListener('click', close);
    modal.addEventListener('click', e => { if (e.target === modal) close(); });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.style.display !== 'none') close();
    });
  },

  bindWishlist() {
    const btn = $('#wishlist-btn');
    if (!btn) return;
    const handle = btn.dataset.handle;
    const saved = Wishlist.has(handle);
    btn.classList.toggle('is-saved', saved);
    btn.querySelector('.js-wishlist-label').textContent = saved ? 'WISHLIST ✓' : 'WISHLIST +';
    btn.addEventListener('click', () => {
      const nowSaved = Wishlist.toggle(handle, this.product);
      btn.classList.toggle('is-saved', nowSaved);
      btn.querySelector('.js-wishlist-label').textContent = nowSaved ? 'WISHLIST ✓' : 'WISHLIST +';
    });
  }
};

/* ── Phase 1 Fit Finder ─────────────────────────────────────── */
const FitFinder = {
  key: 'chess-fit-recommendation',
  sizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'],
  productPage: null,

  init(productPage) {
    const modal = $('#fit-finder-modal');
    const productRoot = $('.product-page');
    if (!modal || !productRoot) return;
    this.productPage = productPage;
    this.modal = modal;
    this.panel = $('.fit-finder-modal__panel', modal);
    this.form = $('.js-fit-finder-form', modal);
    this.result = $('.js-fit-finder-result', modal);
    this.error = $('.js-fit-finder-error', modal);
    this.line = $('.js-fit-recommendation-line');
    this.rules = this.readRules(productRoot);
    this.bind();
    this.applyStored();
  },

  readRules(root) {
    const range = (root.dataset.sizeRange || '')
      .split(',')
      .map(size => size.trim().toUpperCase())
      .filter(size => this.sizes.includes(size));
    return {
      productType: (root.dataset.productType || '').toLowerCase(),
      fitRuns: (root.dataset.fitRuns || 'true-to-size').toLowerCase(),
      compression: (root.dataset.compressionLevel || 'medium').toLowerCase(),
      stretch: (root.dataset.stretchLevel || 'medium').toLowerCase(),
      intendedFit: (root.dataset.intendedFit || 'true-to-size').toLowerCase(),
      sizeRange: range.length ? range : this.sizes
    };
  },

  bind() {
    $$('.js-open-fit-finder').forEach(btn => {
      btn.addEventListener('click', () => this.open(btn));
    });
    $('.js-close-fit-finder', this.modal)?.addEventListener('click', () => this.close());
    this.modal.addEventListener('click', event => {
      if (event.target === this.modal) this.close();
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !this.modal.hidden) this.close();
    });
    $('.js-height-unit', this.modal)?.addEventListener('change', event => {
      const imperial = event.target.value === 'ft';
      $$('.js-height-cm', this.modal).forEach(el => { el.hidden = imperial; });
      $$('.js-height-ft', this.modal).forEach(el => { el.hidden = !imperial; });
    });
    this.form?.addEventListener('submit', event => {
      event.preventDefault();
      this.calculate(new FormData(this.form));
    });
    $('.js-fit-recalculate', this.modal)?.addEventListener('click', () => {
      this.result.hidden = true;
      this.form.hidden = false;
      this.clearError();
      this.form.querySelector('select, input')?.focus();
    });
    $('.js-fit-view-size', this.modal)?.addEventListener('click', () => {
      this.close();
      $('.product-option--size')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  },

  open(trigger) {
    this.lastTrigger = trigger;
    this.modal.hidden = false;
    document.body.classList.add('no-scroll');
    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
      this.panel?.focus();
    });
    trapFocus(this.modal);
  },

  close() {
    this.modal.classList.remove('is-open');
    this.modal.hidden = true;
    document.body.classList.remove('no-scroll');
    this.lastTrigger?.focus();
  },

  clearError() {
    if (!this.error) return;
    this.error.hidden = true;
    this.error.textContent = '';
  },

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  },

  calculate(data) {
    this.clearError();
    const usualSize = String(data.get('usual_size') || '').toUpperCase();
    const preferredFit = String(data.get('preferred_fit') || '');
    if (!usualSize || !preferredFit) {
      this.showError('Please select your usual clothing size and preferred fit.');
      return;
    }

    const recommendation = this.recommend({
      usualSize,
      preferredFit,
      bodyShape: String(data.get('body_shape') || ''),
      heightCm: this.normalizedHeight(data),
      weightKg: this.normalizedWeight(data),
      age: Number(data.get('age') || 0)
    });

    sessionStorage.setItem(this.key, JSON.stringify(recommendation));
    this.renderResult(recommendation);
    this.applyRecommendation(recommendation);
  },

  normalizedHeight(data) {
    if (data.get('height_unit') === 'ft') {
      const ft = Number(data.get('height_ft') || 0);
      const inches = Number(data.get('height_in') || 0);
      return ft ? Math.round(((ft * 12) + inches) * 2.54) : null;
    }
    const cm = Number(data.get('height_cm') || 0);
    return cm || null;
  },

  normalizedWeight(data) {
    const weight = Number(data.get('weight') || 0);
    if (!weight) return null;
    return data.get('weight_unit') === 'lb' ? Math.round(weight * 0.453592) : weight;
  },

  recommend(input) {
    const allowed = this.rules.sizeRange;
    let index = this.sizes.indexOf(input.usualSize);
    if (index < 0) index = this.sizes.indexOf('S');

    let adjustment = 0;
    const reasons = [];
    const runsSmall = ['small', 'runs-small'].includes(this.rules.fitRuns);
    const runsLarge = ['large', 'runs-large'].includes(this.rules.fitRuns);
    const highCompression = ['high', 'firm', 'compressive'].includes(this.rules.compression);

    if (runsSmall && input.preferredFit !== 'compressive') {
      adjustment += 1;
      reasons.push('this garment is set to run small');
    }
    if (runsLarge && input.preferredFit !== 'relaxed') {
      adjustment -= 1;
      reasons.push('this garment is set to run large');
    }
    if (input.preferredFit === 'relaxed' && highCompression) {
      adjustment += 1;
      reasons.push('you prefer a less compressive fit');
    }
    if (input.preferredFit === 'compressive' && !highCompression && !runsSmall) {
      adjustment -= 1;
      reasons.push('you prefer a more sculpted fit');
    }
    if (input.bodyShape === 'hips' && highCompression && input.preferredFit !== 'compressive') {
      reasons.push('you noted more fullness through the hips');
    }

    const adjusted = this.clampToAllowed(index + adjustment, allowed);
    const size = this.sizes[adjusted];
    const changed = size !== input.usualSize;
    const confidence = this.confidence(changed, reasons, allowed);
    const note = reasons.length ? ` We adjusted for ${reasons.join(' and ')}.` : '';
    const explanation = `Based on your usual clothing size and preferred fit, we recommend size ${size}.${note} This is a fit recommendation, not a guarantee.`;

    return {
      size,
      confidence,
      explanation,
      productType: this.rules.productType,
      createdAt: Date.now()
    };
  },

  clampToAllowed(index, allowed) {
    let next = Math.min(this.sizes.length - 1, Math.max(0, index));
    if (allowed.includes(this.sizes[next])) return next;
    const allowedIndexes = allowed.map(size => this.sizes.indexOf(size)).filter(i => i >= 0);
    if (!allowedIndexes.length) return next;
    return allowedIndexes.reduce((closest, candidate) => (
      Math.abs(candidate - next) < Math.abs(closest - next) ? candidate : closest
    ), allowedIndexes[0]);
  },

  confidence(changed, reasons, allowed) {
    const availableSizeValues = $$('.product-option--size .js-option-btn').map(btn => btn.dataset.optionValue?.toUpperCase());
    if (!availableSizeValues.length) return 'More information required';
    if (changed || reasons.length > 1) return 'Likely match';
    if (allowed.length < 3) return 'Between sizes';
    return 'Strong match';
  },

  renderResult(recommendation) {
    $('.js-fit-result-size', this.modal).textContent = recommendation.size;
    $('.js-fit-result-confidence', this.modal).textContent = recommendation.confidence;
    $('.js-fit-result-copy', this.modal).textContent = recommendation.explanation;
    this.form.hidden = true;
    this.result.hidden = false;
  },

  applyStored() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.key) || 'null');
      if (stored?.size) this.applyRecommendation(stored);
    } catch (_error) {
      sessionStorage.removeItem(this.key);
    }
  },

  applyRecommendation(recommendation) {
    $$('.product-option--size .js-option-btn').forEach(btn => {
      btn.classList.toggle('is-recommended', btn.dataset.optionValue?.toUpperCase() === recommendation.size);
    });
    if (this.line) {
      this.line.textContent = `Recommended for you: ${recommendation.size}`;
      this.line.hidden = false;
    }
  }
};

/* ── Hero video mobile play ──────────────────────────────────── */
const HeroVideo = {
  init() {
    const video = $('.js-hero-video-mobile');
    const hero  = $('.js-hero');
    if (!video || !hero) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    });
    observer.observe(hero);
    const play = () => { if (!document.hidden) video.play().catch(() => {}); };
    window.addEventListener('touchstart', play, { once: true, passive: true });
    document.addEventListener('visibilitychange', play);
  }
};

/* ── Accessibility panel ─────────────────────────────────────── */
const A11y = {
  textKey: 'chess-text-size',
  applyTextSize(value) {
    const size = Math.min(140, Math.max(85, Number(value) || 100));
    document.documentElement.style.setProperty('--text-scale', size / 100);
    document.documentElement.style.fontSize = `${size}%`;
    const label = $('.js-a11y-text-value');
    if (label) label.textContent = `${size}%`;
    localStorage.setItem(this.textKey, String(size));
  },
  init() {
    /* Reading Mask was retired: always remove any previously saved version. */
    localStorage.removeItem('chess-a11y-a11y-reading-mask');
    document.body.classList.remove('a11y-reading-mask');
    const trigger = $('.js-a11y-trigger');
    const drawer  = $('#a11y-drawer');
    const closeBtn = $('.js-a11y-close');
    if (trigger && drawer) {
      const close = (restoreFocus = true) => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('a11y-panel-open');
        if (restoreFocus) trigger.focus();
      };
      const open = () => {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        drawer.focus();
      };
      trapFocus(drawer);
      trigger.addEventListener('click', () => drawer.classList.contains('is-open') ? close() : open());
      if (closeBtn) closeBtn.addEventListener('click', () => close());
      document.addEventListener('click', e => {
        if (!e.target.closest('.a11y-panel')) {
          close(false);
        }
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      });
    }
    const themeToggle = $('.js-a11y-theme');
    if (themeToggle) themeToggle.addEventListener('click', () => Theme.toggle());
    this.applyTextSize(localStorage.getItem(this.textKey) || 100);
    $$('.js-a11y-text').forEach(btn => {
      btn.addEventListener('click', () => {
        const current = Number(localStorage.getItem(this.textKey) || 100);
        this.applyTextSize(current + Number(btn.dataset.step || 0));
      });
    });
    $$('.js-a11y-toggle').forEach(btn => {
      const cls = btn.dataset.class;
      if (!cls) return;
      const stored = localStorage.getItem(`chess-a11y-${cls}`);
      if (stored === 'true') { document.body.classList.add(cls); btn.setAttribute('aria-checked', 'true'); }
      btn.addEventListener('click', () => {
        const active = document.body.classList.toggle(cls);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');
        localStorage.setItem(`chess-a11y-${cls}`, active ? 'true' : 'false');
      });
    });
    const updateReadingPosition = y => {
      const clamped = Math.max(72, Math.min(window.innerHeight - 72, y));
      document.documentElement.style.setProperty('--a11y-reading-y', `${clamped}px`);
    };
    document.addEventListener('pointermove', e => updateReadingPosition(e.clientY), { passive: true });
    document.addEventListener('focusin', e => {
      if (!document.body.classList.contains('a11y-reading-guide')) return;
      const rect = e.target.getBoundingClientRect?.();
      if (rect) updateReadingPosition(rect.top + (rect.height / 2));
    });
    const reset = $('.js-a11y-reset');
    if (reset) reset.addEventListener('click', () => {
      ['a11y-high-contrast','a11y-reduce-motion','a11y-reading-guide','a11y-highlight-links','a11y-focus-visible','a11y-dyslexia','a11y-adhd'].forEach(cls => {
        document.body.classList.remove(cls);
        localStorage.removeItem(`chess-a11y-${cls}`);
      });
      $$('.js-a11y-toggle').forEach(btn => btn.setAttribute('aria-checked', 'false'));
      this.applyTextSize(100);
    });
  }
};

/* ── Account dashboard ──────────────────────────────────────── */
const AccountDashboard = {
  init() {
    const root = $('[data-account-dashboard]');
    if (!root) return;
    const activate = key => {
      $$('[data-account-tab]', root).forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.accountTab === key);
      });
      $$('[data-account-panel]', root).forEach(panel => {
        panel.classList.toggle('is-active', panel.dataset.accountPanel === key);
      });
    };
    $$('[data-account-tab]', root).forEach(btn => {
      btn.addEventListener('click', () => activate(btn.dataset.accountTab));
    });
    const detailsForm = $('.chess-details-form', root);
    if (detailsForm) {
      detailsForm.addEventListener('submit', e => {
        e.preventDefault();
        const button = detailsForm.querySelector('button[type="submit"]');
        if (!button) return;
        const original = button.textContent;
        button.textContent = 'SAVED';
        window.setTimeout(() => { button.textContent = original; }, 1400);
      });
    }
  }
};

/* ── Account entry forms ────────────────────────────────────── */
const AccountEntry = {
  init() {
    const birthdate = $('#customer_birthdate');
    if (!birthdate) return;
    birthdate.addEventListener('input', e => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
      const parts = [];
      if (digits.length > 0) parts.push(digits.slice(0, 2));
      if (digits.length > 2) parts.push(digits.slice(2, 4));
      if (digits.length > 4) parts.push(digits.slice(4, 8));
      e.target.value = parts.join(' / ');
    });
  }
};

/* ── Boot ────────────────────────────────────────────────────── */
function chessInit() {
  document.body.classList.add('js-ready');
  Theme.init();
  Header.init();
  Overlays.init();
  Cart.init();
  Wishlist.init();
  WishlistPage.init();
  Gallery.init();
  Statement.init();
  Search.init();
  ScrollCursor.init();
  ProductPage.init();
  HeroVideo.init();
  Localization.init();
  FooterService.init();
  A11y.init();
  AccountEntry.init();
  AccountDashboard.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', chessInit);
} else {
  chessInit();
}
